import { offlineStore } from './store'
import { safeLogger } from '../logger'
import { upsertResponse, type ResponseValue } from '../services/responses'
import { ApiRequestError } from '../errors'
import { parseDraftId } from './draft-id'

/**
 * Sync Engine [FE.3.1]
 * Background process to sync local drafts to the server.
 */
class SyncEngine {
    private isSyncing = false
    private pollInterval: any = null

    start() {
        if (this.pollInterval) return

        // 1. Initial sync
        this.sync()

        // 2. Poll/Listen for online status
        window.addEventListener('online', () => this.sync())
        this.pollInterval = setInterval(() => {
            if (navigator.onLine) this.sync()
        }, 30000) // Every 30 seconds
    }

    stop() {
        if (this.pollInterval) clearInterval(this.pollInterval)
    }

    /**
     * Replays drafts to the Edge Function.
     */
    async sync() {
        if (this.isSyncing || !navigator.onLine) return
        // F-04: never replay offline drafts without an authenticated owner binding
        if (!offlineStore.getActiveOwner()) return

        this.isSyncing = true
        try {
            const drafts = await offlineStore.getAllDrafts()
            if (drafts.length === 0) return

            safeLogger.logEvent('sync_started', { status: drafts.length })
            const processedDraftIds = new Set<string>()

            for (const draft of drafts) {
                const rawDraftId = String(draft.id)
                if (processedDraftIds.has(rawDraftId)) {
                    continue
                }
                processedDraftIds.add(rawDraftId)

                const parsed = parseDraftId(rawDraftId)
                if (!parsed) {
                    safeLogger.logError('sync_invalid_draft_id', crypto.randomUUID())
                    await offlineStore.removeDraft(rawDraftId)
                    continue
                }

                try {
                    const content = (draft as { content?: { response_value?: ResponseValue; notes?: string } }).content
                    if (!content?.response_value) {
                        safeLogger.logError('sync_missing_response_value', crypto.randomUUID())
                        await offlineStore.removeDraft(rawDraftId)
                        continue
                    }
                    await upsertResponse({
                        visitId: parsed.visitId,
                        templateId: parsed.templateId,
                        responseValue: content.response_value,
                        notes: content.notes,
                    })

                    await offlineStore.removeDraft(rawDraftId)
                    safeLogger.logEvent('sync_success', { status: 200 })
                } catch (error) {
                    if (
                        error instanceof ApiRequestError &&
                        (error.status === 409 || error.code === 'conflict')
                    ) {
                        await offlineStore.removeDraft(rawDraftId)
                        safeLogger.logEvent('sync_conflict_resolved', { status: 409 })
                        continue
                    }
                    safeLogger.logError('sync_failed', crypto.randomUUID())
                }
            }
        } finally {
            this.isSyncing = false
        }
    }
}

export const syncEngine = new SyncEngine()
